import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
    ColumnOrder,
    ExpensesSortableFields,
    useAccountPageQuery,
    useExpensesPageQuery,
} from '../../../../../services';
import {
    Continued,
    CTableWrapper,
    DateRange,
    DBlock,
    DDateRangePicker,
    DErrorCard,
    Discontinued,
    DPage,
    DPageBody,
    DPageHeader,
    DPageLoader,
    EditIcon,
} from '../../../../dum';
import { SUpsertAccountDialog } from '../../../../smart';
import { SUpsertExpenseDialog, SUpsertOrderSale } from '../../../../smart';
import {
    BUSINESS_TZ,
    DATE_FORMAT,
    formatFloat,
    getBusinessDateFromInstant,
    getDateFromString,
    round,
} from '../../../../../helpers';
import dayjs, { Dayjs } from 'dayjs';
import {
    Box,
    Chip,
    IconButton,
    Tab,
    Tabs,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    VirtualColumn,
    VirtualTransactionTable,
} from './blocks/virtual-transaction-table';
import {
    AccountLedgerExcelExportButton,
    AccountExpenseExportGroup,
    AccountLedgerExportRow,
} from './blocks/account-ledger-excel-export-button';

type TAccountPageParams = {
    accountId: string;
};

type RowKind = 'transaction' | 'transfer';

interface FlatRow {
    kind: RowKind;
    id: number;
    // transaction-only
    type: 'sale' | 'expense' | null;
    order_code: string;
    invoice_code: number | null;
    receipt_type_id: number | null;
    receiptTypeAppliesTax: boolean;
    total_with_tax: number;
    transfer_receipts_total: number;
    // shared
    date: Dayjs | null;
    notes: string;
    // transfer-only
    transferAmount: number;
    // a transfer dated before its parent sale/expense is an advance ("anticipo")
    isAnticipo: boolean;
    // parent sale/expense folio echoed onto the transfer row, for linkage
    parentFolio: string;
    // index of the sale/expense block this row belongs to (drives table banding)
    groupId: number;
    // computed running account balance (oldest -> newest)
    cumulative_amount: number;
}

export function AccountPage({
    viewOnly = false,
}: { viewOnly?: boolean } = {}) {
    const params = useParams<TAccountPageParams>();
    const accountId = params.accountId ? Number(params.accountId) : 0;
    const businessToday = dayjs().tz(BUSINESS_TZ);

    // Default the ledger date range to this year (1 Jan → today) so the query is
    // bounded to recent activity instead of the full account history. This range
    // now drives a SERVER-side filter (below), not a client-side one.
    const [filterDate, setFilterDate] = useState<string | null>(
        businessToday.startOf('year').format(DATE_FORMAT),
    );
    const [untilDate, setUntilDate] = useState<string | null>(
        businessToday.format(DATE_FORMAT),
    );

    // The date range is sent to the backend, which returns only in-range
    // transactions plus the carried-forward opening balance. `previousData`
    // keeps the last result on screen while a new range is fetched, so changing
    // the dates doesn't flash the whole page back to a loader.
    const { data, previousData, loading, error, refetch } =
        useAccountPageQuery({
            variables: {
                AccountId: accountId,
                From: filterDate,
                Until: untilDate,
            },
        });

    const accountData = data ?? previousData;

    const { data: expenseExportData, loading: expenseExportLoading } =
        useExpensesPageQuery({
            variables: {
                skip: 0,
                take: 5000,
                startDate: filterDate,
                endDate: untilDate
                    ? dayjs.utc(untilDate).add(1, 'day').format(DATE_FORMAT)
                    : null,
                filter: '',
                accountId,
                resourceId: null,
                sortField: ExpensesSortableFields.Date,
                sortOrder: ColumnOrder.Desc,
                noReceipt: false,
                noSupplement: false,
                isTransferComplete: false,
            },
            skip:
                viewOnly ||
                accountId === 0 ||
                accountData?.getAccount?.is_supplier !== true,
        });

    // Which tab is selected. The Balance (ledger) tab always shows; the Cliente
    // tab only when is_client. The available tabs are computed once data loads,
    // so this selection is reconciled against them at render time.
    const [activeTab, setActiveTab] = useState<
        'client' | 'supplier' | 'balance' | null
    >(null);
    const [upsertDialog, setUpsertDialog] = useState(false);
    const [openSale, setOpenSale] = useState(false);
    const [openExpense, setOpenExpense] = useState(false);
    const [selectedId, setSelectedId] = useState<number | null>(null);

    const { mainRows, pendingTotal } = useMemo(() => {
        const transactions = accountData?.getAccountTransactionHistory ?? [];
        const transfers = accountData?.getAccountTransfers ?? [];
        const taxableReceiptTypeIds = new Set(
            (accountData?.getReceiptTypes ?? [])
                .filter((receiptType) => receiptType.applies_tax)
                .map((receiptType) => receiptType.id),
        );
        // Net balance from all activity before the range — the carried-forward
        // opening balance the running total builds on (computed server-side).
        const openingBalance = accountData?.getAccountOpeningBalance ?? 0;

        // Every sale/expense and every transfer is its own ledger row carrying a
        // signed delta: a transaction adds its total, a transfer (payment)
        // subtracts its amount. Rows are ordered purely by date and the running
        // balance is their chronological accumulation seeded by the opening
        // balance — transfers are NOT grouped under their transaction.
        type Row = Omit<FlatRow, 'cumulative_amount' | 'groupId'> & {
            delta: number;
        };

        const txRows: Row[] = transactions.map((item) => {
            const txDate = item.date
                ? dayjs.utc(getDateFromString(item.date))
                : null;
            return {
                kind: 'transaction',
                id: item.id,
                type: item.type as 'sale' | 'expense',
                order_code: item.order_code,
                invoice_code: item.invoice_code ?? null,
                receipt_type_id: item.receipt_type_id ?? null,
                receiptTypeAppliesTax: taxableReceiptTypeIds.has(
                    item.receipt_type_id ?? 0,
                ),
                total_with_tax: item.total_with_tax,
                transfer_receipts_total: item.transfer_receipts_total,
                date: txDate,
                notes: item.notes,
                transferAmount: 0,
                isAnticipo: false,
                parentFolio: '',
                delta: item.total_with_tax,
            };
        });

        const transferRows: Row[] = transfers.map((t) => {
            const parentDate = t.parent_date
                ? dayjs.utc(getDateFromString(t.parent_date))
                : null;
            const tDate = t.transferred_date
                ? dayjs.utc(
                      getBusinessDateFromInstant(t.transferred_date),
                  )
                : parentDate;
            // An advance: the money moved before the sale/expense was dated.
            const isAnticipo = !!(
                tDate &&
                parentDate &&
                tDate.isBefore(parentDate, 'day')
            );
            // The folio of the sale/expense this payment belongs to.
            const parentFolio =
                t.parent_type === 'sale' &&
                taxableReceiptTypeIds.has(t.parent_receipt_type_id ?? 0) &&
                t.parent_invoice_code
                    ? `${t.parent_order_code} F-${t.parent_invoice_code}`
                    : t.parent_order_code;
            return {
                kind: 'transfer',
                id: 0,
                type: null,
                order_code: '',
                invoice_code: null,
                receipt_type_id: null,
                receiptTypeAppliesTax: false,
                total_with_tax: 0,
                transfer_receipts_total: 0,
                date: tDate,
                notes: t.notes ?? '',
                transferAmount: t.amount,
                isAnticipo,
                parentFolio,
                delta: -t.amount,
            };
        });

        // Order every row by date ASCENDING (the order the running balance is
        // accumulated in). On a tie the transaction sits above its payments;
        // null dates sink to the bottom.
        const ascByDate = (a: Row, b: Row) => {
            if (!a.date && !b.date) return 0;
            if (!a.date) return 1;
            if (!b.date) return -1;
            if (a.date.isSame(b.date)) {
                if (a.kind !== b.kind) {
                    return a.kind === 'transaction' ? -1 : 1;
                }
                return 0;
            }
            return a.date.isBefore(b.date) ? -1 : 1;
        };

        // Main statement: every transaction and transfer, in date order, with a
        // continuous running balance. Displayed newest-first.
        let balance = openingBalance;
        const walked: FlatRow[] = [...txRows, ...transferRows]
            .sort(ascByDate)
            .map((row) => {
                balance += row.delta;
                const { delta, ...rest } = row;
                return { ...rest, groupId: 0, cumulative_amount: balance };
            });
        const mainRows = [...walked].reverse();

        // The pendiente headline is driven by the transactions alone — their
        // transfer completeness is intrinsic (the full transferred total),
        // independent of how rows are ordered or dated.
        const txOnly: FlatRow[] = txRows.map((row) => {
            const { delta, ...rest } = row;
            return { ...rest, groupId: 0, cumulative_amount: 0 };
        });

        // Nothing transferred yet — oldest first.
        const untransferredRows = txOnly.filter(
            (row) => row.transfer_receipts_total === 0,
        );
        const untransferredTotal = untransferredRows.reduce(
            (sum, row) => sum + row.total_with_tax,
            0,
        );

        // Partially transferred: something paid, but not the full total. Newest
        // first.
        const unbalancedRows = [...txOnly]
            .reverse()
            .filter(
                (row) =>
                    row.transfer_receipts_total > 0 &&
                    round(row.total_with_tax - row.transfer_receipts_total) !==
                        0,
            );
        // "Restante": money still owed on the partially-transferred items.
        const partialRemaining = round(
            unbalancedRows.reduce(
                (sum, row) =>
                    sum + (row.total_with_tax - row.transfer_receipts_total),
                0,
            ),
        );
        // "Total pendiente": everything still owed — the nothing-transferred
        // total plus what is still owed on the partially-transferred items. This
        // is the only surviving headline; the per-document pendientes table was
        // folded into the single ledger below.
        const pendingTotal = partialRemaining + untransferredTotal;

        return {
            mainRows,
            pendingTotal,
        };
    }, [accountData]);

    const openTransactionDialog = (row: FlatRow) => {
        if (row.kind !== 'transaction') return;
        setSelectedId(row.id);
        if (row.type === 'sale') setOpenSale(true);
        else setOpenExpense(true);
    };

    const typeChip = (row: FlatRow) => {
        if (row.kind === 'transfer') {
            return (
                <Chip
                    size="small"
                    label={row.isAnticipo ? 'Anticipo' : 'Transferencia'}
                    color={row.isAnticipo ? 'warning' : 'info'}
                    variant="outlined"
                />
            );
        }
        return (
            <Chip
                size="small"
                label={row.type === 'sale' ? 'Venta' : 'Gasto'}
                color={row.type === 'sale' ? 'success' : 'error'}
                variant="outlined"
            />
        );
    };

    // Plain-text versions of the type and folio, shared by the on-screen cells
    // and the Excel export so both read identically.
    const tipoText = (row: FlatRow) =>
        row.kind === 'transfer'
            ? row.isAnticipo
                ? 'Anticipo'
                : 'Transferencia'
            : row.type === 'sale'
            ? 'Venta'
            : 'Gasto';

    const folioText = (row: FlatRow) =>
        row.kind === 'transfer'
            ? row.parentFolio
            : row.type === 'sale' &&
              row.receiptTypeAppliesTax &&
              row.invoice_code
            ? `${row.order_code} F-${row.invoice_code}`
            : row.order_code;

    const folio = (row: FlatRow) => {
        if (row.kind === 'transfer') {
            return row.parentFolio ? (
                <Typography
                    variant="body2"
                    noWrap
                    sx={{ color: 'text.secondary', fontStyle: 'italic' }}
                >
                    ↳ {row.parentFolio}
                </Typography>
            ) : null;
        }
        return folioText(row);
    };

    const notesCell = (row: FlatRow) => (
        <Typography
            variant="body2"
            noWrap
            sx={
                row.kind === 'transfer'
                    ? { pl: 2, color: 'text.secondary', fontStyle: 'italic' }
                    : undefined
            }
        >
            {row.notes}
        </Typography>
    );

    // Columns for the main ledger (with transfers + running balance).
    const mainColumns: VirtualColumn<FlatRow>[] = [
        { title: 'Tipo', content: typeChip },
        { title: 'Folio', content: folio },
        {
            title: 'Fecha',
            textAlign: 'center',
            content: (row) => (row.date ? row.date.format(DATE_FORMAT) : ''),
        },
        { title: 'Notas', cellGrow: 3, textAlign: 'left', content: notesCell },
        {
            title: 'Total',
            textAlign: 'right',
            content: (row) =>
                row.kind === 'transfer' ? null : formatFloat(row.total_with_tax),
        },
        {
            title: 'Transferido',
            textAlign: 'right',
            // Blank (not "0") on transaction rows — a sale/expense has no
            // transferred amount of its own, mirroring how the Total column is
            // left blank on transfer rows.
            content: (row) =>
                row.kind === 'transfer'
                    ? formatFloat(row.transferAmount)
                    : null,
        },
        {
            title: 'Acumulado',
            textAlign: 'right',
            content: (row) => formatFloat(row.cumulative_amount),
        },
    ];

    if (loading && !accountData) return <DPageLoader />;
    // getAccount is null for a not-found id — and, for a client-restricted
    // (Ventas) caller, for any non-client account — so guard before using it.
    if (error || !accountData || !accountData.getAccount)
        return <DErrorCard />;

    const account = accountData.getAccount;
    // Sort the product catalog first by product category name, then by product
    // name — same ordering as the account upsert dialog's "Cliente" tab.
    const catalog = [...(accountData.getAccountProducts ?? [])].sort((a, b) => {
        const byCategory = (
            a.product?.product_category?.name ?? ''
        ).localeCompare(b.product?.product_category?.name ?? '');
        if (byCategory !== 0) return byCategory;
        return (a.product?.compound_description ?? '').localeCompare(
            b.product?.compound_description ?? '',
        );
    });
    const resourceCatalog = accountData.getAccountResources ?? [];

    // Balance (the transaction ledger) is always available; the Cliente tab
    // (product catalog) only when the account is a client. The selected tab
    // falls back to the first available one when the stored selection isn't
    // applicable (or hasn't been set yet).
    const availableTabs: {
        key: 'client' | 'supplier' | 'balance';
        label: string;
    }[] = [];
    if (account.is_client)
        availableTabs.push({ key: 'client', label: 'Catálogo de productos' });
    if (account.is_supplier)
        availableTabs.push({ key: 'supplier', label: 'Catálogo de recursos' });
    availableTabs.push({ key: 'balance', label: 'Balance' });

    const effectiveTab =
        activeTab && availableTabs.some((t) => t.key === activeTab)
            ? activeTab
            : availableTabs[0]?.key ?? null;

    // Flatten the visible ledger into plain Excel rows. Numeric cells are null
    // where the column doesn't apply, so the sheet leaves them blank.
    const excelRows: AccountLedgerExportRow[] = mainRows.map((row) => ({
        tipo: tipoText(row),
        folio: folioText(row),
        fecha: row.date ? row.date.format(DATE_FORMAT) : '',
        notas: row.notes,
        total: row.kind === 'transfer' ? null : row.total_with_tax,
        transferido: row.kind === 'transfer' ? row.transferAmount : null,
        acumulado: row.cumulative_amount,
    }));

    // Reuse the existing expenses query to add resource detail to the export;
    // matching by the ledger's expense ids keeps it within the selected range.
    const expenseIds = new Set(
        mainRows
            .filter(
                (row) =>
                    row.kind === 'transaction' && row.type === 'expense',
            )
            .map((row) => row.id),
    );
    const excelExpenseGroups: AccountExpenseExportGroup[] =
        expenseExportData?.paginatedExpenses.docs
            .filter((expense) => expenseIds.has(expense.id))
            .map((expense) => ({
                folio: expense.external_code,
                fecha: expense.date
                    ? dayjs.utc(getDateFromString(expense.date))
                          .format(DATE_FORMAT)
                    : '',
                notas: expense.notes,
                total: expense.total_with_tax,
                resources: expense.expense_resources.map((resource) => ({
                    name: resource.resource?.name ?? '',
                    units: resource.units,
                    unitPrice: resource.unit_price ?? 0,
                    notes: resource.notes,
                })),
            })) ?? [];

    // The export summary only needs the final outstanding balance.
    const excelSummary = {
        balance: pendingTotal,
    };

    return (
        <DPage animated>
            <DPageHeader title={account.name} />
            <DPageBody disableGutters>
                <Tabs
                    value={effectiveTab ?? false}
                    onChange={(_e, value) => setActiveTab(value)}
                    sx={{ mb: 3 }}
                >
                    {availableTabs.map((t) => (
                        <Tab key={t.key} value={t.key} label={t.label} />
                    ))}
                </Tabs>

                {/* Cliente (is_client): the per-account product catalog. */}
                {effectiveTab === 'client' ? (
                    <DBlock>
                        <CTableWrapper
                            data={catalog}
                            items={[
                                {
                                    title: 'Producto',
                                    cellGrow: 45,
                                    content: (ap) =>
                                        ap.product?.compound_description ?? '',
                                },
                                {
                                    title: 'Categoría',
                                    cellGrow: 15,
                                    content: (ap) =>
                                        ap.product?.product_category?.name ??
                                        '',
                                },
                                {
                                    title: 'Precio por kilo',
                                    textAlign: 'right',
                                    cellGrow: 15,
                                    content: (ap) => formatFloat(ap.kilo_price),
                                },
                                {
                                    title: 'Precio por bulto',
                                    textAlign: 'right',
                                    cellGrow: 15,
                                    content: (ap) => formatFloat(ap.group_price),
                                },
                                {
                                    title: 'Peso',
                                    textAlign: 'right',
                                    cellGrow: 10,
                                    content: (ap) =>
                                        formatFloat(ap.group_weight),
                                },
                                {
                                    title: 'Estatus',
                                    textAlign: 'center',
                                    cellGrow: 10,
                                    content: (ap) =>
                                        ap.product?.discontinued ? (
                                            <Discontinued color={'error'} />
                                        ) : (
                                            <Continued color={'success'} />
                                        ),
                                },
                            ]}
                        />
                    </DBlock>
                ) : null}

                {/* Proveedor (is_supplier): the per-account resource catalog.
                    Guideline-only — resource, unit price and free-form notes. */}
                {effectiveTab === 'supplier' ? (
                    <DBlock>
                        <CTableWrapper
                            data={resourceCatalog}
                            items={[
                                {
                                    title: 'Recurso',
                                    cellGrow: 55,
                                    content: (ar) => ar.resource?.name ?? '',
                                },
                                {
                                    title: '$ unitario',
                                    textAlign: 'right',
                                    cellGrow: 15,
                                    content: (ar) => formatFloat(ar.unit_price),
                                },
                                {
                                    title: 'Notas',
                                    textAlign: 'left',
                                    cellGrow: 30,
                                    content: (ar) => ar.notes ?? '',
                                },
                            ]}
                        />
                    </DBlock>
                ) : null}

                {/* Balance: the transaction ledger (always available). */}
                {effectiveTab === 'balance' ? (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 4,
                        // The two ledger tables have fixed heights (400 + 560),
                        // so stacked with the toolbar and totals they run past
                        // the viewport. DPageBody is height-bounded by the 100vh
                        // app shell, so making this region flex-fill + scroll
                        // internally keeps it on the page instead of overflowing
                        // it — the same fix as the sticky-header tables.
                        flex: 1,
                        minHeight: 0,
                        overflowY: 'auto',
                        pr: 1,
                    }}
                >
                    {/* Top toolbar: date range filter on the left; the
                        outstanding-balance headline (Total pendiente), the Excel
                        export and the edit-account button grouped on the right. */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 2,
                            flexWrap: 'wrap',
                        }}
                    >
                        <Box sx={{ width: 280 }}>
                            <DDateRangePicker
                                label="Fecha"
                                value={
                                    filterDate && untilDate
                                        ? [
                                              dayjs(filterDate, DATE_FORMAT),
                                              dayjs(untilDate, DATE_FORMAT),
                                          ]
                                        : undefined
                                }
                                onChange={(range: DateRange) => {
                                    setFilterDate(range[0].format(DATE_FORMAT));
                                    setUntilDate(range[1].format(DATE_FORMAT));
                                }}
                                onClear={() => {
                                    setFilterDate(null);
                                    setUntilDate(null);
                                }}
                                disableGutters
                            />
                        </Box>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                            }}
                        >
                            <Box sx={{ textAlign: 'right' }}>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                >
                                    Total pendiente
                                </Typography>
                                <Typography variant="h6">
                                    {formatFloat(pendingTotal)}
                                </Typography>
                            </Box>
                            <AccountLedgerExcelExportButton
                                mainRows={excelRows}
                                expenseGroups={excelExpenseGroups}
                                summary={excelSummary}
                                fileName={`estado-de-cuenta-${account.name}.xlsx`}
                                disabled={
                                    expenseExportLoading ||
                                    excelRows.length === 0
                                }
                            />
                            {!viewOnly ? (
                                <Tooltip title="Editar cuenta">
                                    <IconButton
                                        size="small"
                                        onClick={() => setUpsertDialog(true)}
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            ) : null}
                        </Box>
                    </Box>

                    {/* Single ledger — every transaction and transfer, in date
                        order, with the running balance */}
                    <DBlock>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                mb: 1,
                            }}
                        >
                            <Typography variant="h6">
                                Estado de cuenta
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {
                                    mainRows.filter(
                                        (r) => r.kind === 'transaction',
                                    ).length
                                }{' '}
                                concepto
                                {mainRows.filter(
                                    (r) => r.kind === 'transaction',
                                ).length === 1
                                    ? ''
                                    : 's'}
                            </Typography>
                        </Box>
                        <VirtualTransactionTable
                            columns={mainColumns}
                            data={mainRows}
                            height={560}
                            onRowDoubleClick={
                                viewOnly ? undefined : openTransactionDialog
                            }
                            contextItems={
                                viewOnly
                                    ? []
                                    : [
                                          {
                                              title: 'Editar',
                                              disable: (row) =>
                                                  row.kind !== 'transaction',
                                              callback: openTransactionDialog,
                                          },
                                      ]
                            }
                            rowSx={(row) =>
                                row.kind === 'transfer'
                                    ? { opacity: 0.75 }
                                    : {}
                            }
                            getRowKey={(_row, index) => `m-${index}`}
                        />
                    </DBlock>
                </Box>
                ) : null}
            </DPageBody>
            {!viewOnly ? (
                <>
                    <SUpsertAccountDialog
                        open={upsertDialog}
                        setOpen={setUpsertDialog}
                        accountId={account.id}
                    />
                    <SUpsertOrderSale
                        open={openSale}
                        setOpen={(o) => {
                            setOpenSale(o);
                            if (!o) void refetch();
                        }}
                        orderSaleId={selectedId}
                    />
                    <SUpsertExpenseDialog
                        open={openExpense}
                        setOpen={(o) => {
                            setOpenExpense(o);
                            if (!o) void refetch();
                        }}
                        expenseId={selectedId}
                    />
                </>
            ) : null}
        </DPage>
    );
}
